export const WALLET_FEATURE_ID = "access.wallet"

export enum WalletType {
    APPLE_WALLET = "APPLE_WALLET",
    GOOGLE_WALLET = "GOOGLE_WALLET"
}

export const WALLET_TYPE_LABELS: Record<WalletType, string> = {
    [WalletType.APPLE_WALLET]: "Apple Wallet",
    [WalletType.GOOGLE_WALLET]: "Google Wallet"
}

export enum DeviceType {
    APPLE = "APPLE",
    ANDROID = "ANDROID",
    DESKTOP = "DESKTOP"
}

export enum WalletPassStatus {
    CREATED = "CREATED",
    ISSUE_INITIATED = "ISSUE_INITIATED",
    ISSUING = "ISSUING",
    ACTIVE = "ACTIVE",
    SUSPENDING = "SUSPENDING",
    USER_SUSPENDING = "USER_SUSPENDING",
    SUSPENDED = "SUSPENDED",
    USER_SUSPENDED = "USER_SUSPENDED",
    RESUMING = "RESUMING",
    USER_RESUMING = "USER_RESUMING",
    CANCELLED = "CANCELLED",
    REVOKING = "REVOKING",
    USER_REVOKING = "USER_REVOKING",
    REVOKED = "REVOKED",
    USER_REVOKED = "USER_REVOKED",
    ISSUE_FAILED = "ISSUE_FAILED",
    REVOKE_FAILED = "REVOKE_FAILED"
}

export enum WalletPassStatusGroup {
    PENDING = "Pending",
    ACTIVE = "Active",
    SUSPENDED = "Suspended",
    RESUMING = "Resuming",
    REVOKED = "Revoked",
    FAILED = "Failed"
}

export const WALLET_PASS_STATUS_GROUPS: Record<WalletPassStatus, WalletPassStatusGroup> = {
    [WalletPassStatus.CREATED]: WalletPassStatusGroup.PENDING,
    [WalletPassStatus.ISSUE_INITIATED]: WalletPassStatusGroup.PENDING,
    [WalletPassStatus.ISSUING]: WalletPassStatusGroup.PENDING,
    [WalletPassStatus.ACTIVE]: WalletPassStatusGroup.ACTIVE,
    [WalletPassStatus.SUSPENDING]: WalletPassStatusGroup.SUSPENDED,
    [WalletPassStatus.USER_SUSPENDING]: WalletPassStatusGroup.SUSPENDED,
    [WalletPassStatus.SUSPENDED]: WalletPassStatusGroup.SUSPENDED,
    [WalletPassStatus.USER_SUSPENDED]: WalletPassStatusGroup.SUSPENDED,
    [WalletPassStatus.RESUMING]: WalletPassStatusGroup.RESUMING,
    [WalletPassStatus.USER_RESUMING]: WalletPassStatusGroup.RESUMING,
    [WalletPassStatus.CANCELLED]: WalletPassStatusGroup.REVOKED,
    [WalletPassStatus.REVOKING]: WalletPassStatusGroup.REVOKED,
    [WalletPassStatus.USER_REVOKING]: WalletPassStatusGroup.REVOKED,
    [WalletPassStatus.REVOKED]: WalletPassStatusGroup.REVOKED,
    [WalletPassStatus.USER_REVOKED]: WalletPassStatusGroup.REVOKED,
    [WalletPassStatus.ISSUE_FAILED]: WalletPassStatusGroup.FAILED,
    [WalletPassStatus.REVOKE_FAILED]: WalletPassStatusGroup.FAILED
}

export const WALLET_PASS_STATUS_VARIANTS: Record<WalletPassStatusGroup, string> = {
    [WalletPassStatusGroup.PENDING]: "primary",
    [WalletPassStatusGroup.ACTIVE]: "success",
    [WalletPassStatusGroup.SUSPENDED]: "warning",
    [WalletPassStatusGroup.RESUMING]: "info",
    [WalletPassStatusGroup.REVOKED]: "danger",
    [WalletPassStatusGroup.FAILED]: "danger"
}

export const WALLET_PASS_DEVICE_GROUPS: WalletPassStatusGroup[] = [
    WalletPassStatusGroup.ACTIVE,
    WalletPassStatusGroup.SUSPENDED,
    WalletPassStatusGroup.RESUMING
]

export const WALLET_PASS_LISTED_GROUPS: WalletPassStatusGroup[] = [WalletPassStatusGroup.PENDING, ...WALLET_PASS_DEVICE_GROUPS]

export const WALLET_PASS_FAILED_GROUPS: WalletPassStatusGroup[] = [WalletPassStatusGroup.REVOKED, WalletPassStatusGroup.FAILED]

export const WALLET_PASS_ISSUING_STATUSES: WalletPassStatus[] = [WalletPassStatus.ISSUE_INITIATED, WalletPassStatus.ISSUING]

export enum WalletFlowStatus {
    IDLE = "IDLE",
    APPLE_VALIDATION = "APPLE_VALIDATION",
    GOOGLE_VALIDATION = "GOOGLE_VALIDATION",
    SUCCESS = "SUCCESS",
    PENDING = "PENDING",
    FAILED = "FAILED"
}

export enum WalletErrorCode {
    ISSUANCE_TOKEN_EXPIRED = "ISSUANCE_TOKEN_EXPIRED",
    CARD_IN_TOO_MANY_WALLETS = "CARD_IN_TOO_MANY_WALLETS",
    UN_SUPPORTED_REQUEST = "UN_SUPPORTED_REQUEST",
    INVALID_ISSUANCE_TOKEN = "INVALID_ISSUANCE_TOKEN"
}

export const WALLET_PASSES_PAGE_SIZE = 10
export const WALLET_PASSES_MAX_PAGES = 100
export const WALLET_PASSES_CACHE_TTL_MS = 30 * 1000

export interface WalletPassCredential {
    id?: string
    credentialTemplateId?: string
    status?: string
    platformType?: string
    partNumber?: string
    cardNumber?: number
}

export interface WalletPassDevice {
    name?: string
    deviceType?: string
    platform?: string
    status?: string
}

export interface WalletPass {
    passId: string
    status: string
    updateStatus?: string
    passTemplateId?: string
    deviceType?: string
    issuedDateTime?: string
    expiresDateTime?: string
    method?: string | null
    device?: WalletPassDevice
    credentials: WalletPassCredential[]
}

export interface WalletConfig {
    googleClientId: string
    applePartnerId: string
    appleWalletDomain: string
}

export interface WalletPlatformsResponse {
    supportedServiceList: string[]
}

export interface WalletProvisionPayload {
    walletType: WalletType
    googleIdToken?: string
}

export type AppleWalletJws = Record<string, unknown>

export interface WalletProvisionResponse {
    appleWallet?: { jws: AppleWalletJws }
    googleWallet?: { blob: string }
}

export interface AppleWalletResult {
    status: string
    statusMessage?: string
}

export interface AppleWalletJwsResolution {
    state: number
    jws: AppleWalletJws
}

export interface AppleWalletOptions {
    partnerId: string
    domain: string
    buttonId: string
    jwsResolver: () => Promise<AppleWalletJwsResolution>
    resultResolver: (result: AppleWalletResult) => void
}

export interface GoogleCredentialResponse {
    credential: string
}

export interface GoogleIdConfiguration {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    ux_mode?: "popup" | "redirect"
}

export interface GoogleSignInButtonOptions {
    type?: "standard" | "icon"
    theme?: "outline" | "filled_blue" | "filled_black"
    size?: "large" | "medium" | "small"
    text?: "signin_with" | "signup_with" | "continue_with" | "signin"
    shape?: "rectangular" | "pill" | "circle" | "square"
}

export interface GoogleIdentityServices {
    accounts: {
        id: {
            initialize: (configuration: GoogleIdConfiguration) => void
            renderButton: (parent: HTMLElement, options: GoogleSignInButtonOptions) => void
        }
    }
}
